import { useMemo } from "react";
import {
	getEffectiveTransactionDate,
	getSignedCashFlow,
	getSignedUnits,
	humanizeAssetLabel,
	normalizeAsset,
	normalizeDateKey,
	useAssetPalette,
	useInvestmentSummaryQuery,
	useInvestmentsQuery,
} from "../home/shared";

// One asset per group, transactions newest-first, each row carrying the holding
// it left behind. The running balance has to be accumulated oldest-first, so the
// list is built in date order and reversed only for display.
const buildGroups = (investments = [], summaryByAsset = new Map(), paletteFor) => {
	const byAsset = new Map();

	investments.forEach((investment) => {
		const asset = normalizeAsset(investment?.asset);
		const effectiveDate = getEffectiveTransactionDate(investment);
		if (!asset || !effectiveDate) return;

		const rows = byAsset.get(asset) || [];
		rows.push({
			...investment,
			asset,
			effectiveDate,
			transactionType: String(investment?.transactionType || "BUY").toUpperCase(),
		});
		byAsset.set(asset, rows);
	});

	return Array.from(byAsset.entries())
		.map(([asset, rows]) => {
			const ordered = [...rows].sort(
				(left, right) => left.effectiveDate - right.effectiveDate
			);

			let holding = 0;
			let netInvested = 0;
			const withRunningTotals = ordered.map((row) => {
				holding += getSignedUnits(row);
				netInvested += getSignedCashFlow(row);
				return { ...row, holdingAfter: holding };
			});

			// The summary endpoint is already on the dashboard, so average cost and
			// market value cost nothing extra here.
			const summary = summaryByAsset.get(asset) || null;

			return {
				asset,
				label: humanizeAssetLabel(asset),
				palette: paletteFor(asset),
				openUnits: holding,
				netInvested,
				averageCost: summary?.averageCost ?? null,
				currentPrice: summary?.currentPrice ?? null,
				currentWorth: summary?.currentWorth ?? null,
				unrealizedPL: summary?.unrealizedPL ?? null,
				realizedPL: summary?.realizedPL ?? null,
				remainingCostBasis: summary?.remainingCostBasis ?? null,
				rows: withRunningTotals.reverse(),
				firstDate: ordered[0]?.effectiveDate || null,
				lastDate: ordered[ordered.length - 1]?.effectiveDate || null,
			};
		})
		.sort((left, right) => {
			// Anything still held sits above closed positions.
			const openDiff = Number(right.openUnits > 0) - Number(left.openUnits > 0);
			return openDiff || left.label.localeCompare(right.label);
		});
};

export const useTransactionsByAsset = ({
	search = "",
	type = "ALL",
	from = "",
	to = "",
} = {}) => {
	const {
		data: investments,
		isLoading,
		isError,
		refetch,
	} = useInvestmentsQuery();
	const { data: summary } = useInvestmentSummaryQuery();
	const paletteFor = useAssetPalette();

	const summaryByAsset = useMemo(
		() =>
			new Map(
				(summary?.assets || []).map((row) => [normalizeAsset(row.asset), row])
			),
		[summary]
	);

	const groups = useMemo(
		() => buildGroups(investments, summaryByAsset, paletteFor),
		[investments, summaryByAsset, paletteFor]
	);

	const visibleGroups = useMemo(() => {
		const term = search.trim().toLowerCase();
		// Both bounds are inclusive, and comparing YYYY-MM-DD keys keeps a
		// transaction on the boundary day in range whatever time it carries.
		const fromKey = from || null;
		const toKey = to || null;

		return groups
			.map((group) => ({
				...group,
				// The running balance stays whole-ledger even when the view is
				// filtered -- a "holdings after" that only counted buys would lie.
				rows: group.rows.filter((row) => {
					if (type !== "ALL" && row.transactionType !== type) return false;
					if (!fromKey && !toKey) return true;

					const dateKey = normalizeDateKey(row.effectiveDate);
					if (!dateKey) return false;
					if (fromKey && dateKey < fromKey) return false;
					if (toKey && dateKey > toKey) return false;
					return true;
				}),
			}))
			.filter(
				(group) =>
					group.rows.length > 0 &&
					(!term ||
						group.asset.includes(term) ||
						group.label.toLowerCase().includes(term))
			);
	}, [groups, search, type, from, to]);

	const transactionCount = useMemo(
		() => visibleGroups.reduce((total, group) => total + group.rows.length, 0),
		[visibleGroups]
	);

	const totalTransactionCount = useMemo(
		() => groups.reduce((total, group) => total + group.rows.length, 0),
		[groups]
	);

	return {
		groups: visibleGroups,
		assetCount: groups.length,
		transactionCount,
		totalTransactionCount,
		isLoading,
		isError,
		refetch,
	};
};
